import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import BooleanField from '../../../../core/components/Fields/BooleanField.tsx';
import StringField from '../../../../core/components/Fields/StringField.tsx';
import FormField from '../../../../core/components/FormField.tsx';
import { withIcon } from '../../../../core/components/Icon.tsx';
import { SmartInboxConfigStepProps } from '../SmartInboxConfigSteps.tsx';

const SpinnerIcon = withIcon(faSpinnerThird);

const GeneralFormFields: React.FC<
    SmartInboxConfigStepProps & { hideEnabledField?: boolean; hideRelocatedFields?: boolean }
> = ({ form, integration, isLoading, isCreateMode, hideEnabledField = false, hideRelocatedFields = false }) => {
    const { t } = useTranslation('integrations');
    const formErrors = form.formState.errors;

    return (
        <div className="flex flex-col gap-6">
            {isLoading && (
                <div className="w-full h-full flex justify-center items-center">
                    <SpinnerIcon spin className="text-3xl text-content-weak" />
                </div>
            )}
            {!isLoading && (
                <>
                    <Controller
                        name="name"
                        control={form.control}
                        rules={{ required: t('smartInbox.form.validation.nameRequired') }}
                        render={({ field }) => (
                            <FormField label={t('smartInbox.form.name')} error={formErrors.name} required>
                                <StringField
                                    {...field}
                                    placeholder={isCreateMode ? t('smartInbox.form.nameHint') : undefined}
                                />
                            </FormField>
                        )}
                    />

                    {isCreateMode ? (
                        <Controller
                            name="mailInbox"
                            control={form.control}
                            rules={{ required: t('smartInbox.form.validation.mailInboxRequired') }}
                            render={({ field }) => (
                                <FormField label={t('smartInbox.form.mailInbox')} error={formErrors.mailInbox} required>
                                    <StringField
                                        {...field}
                                        type="email"
                                        placeholder={t('smartInbox.form.mailInboxHint')}
                                    />
                                </FormField>
                            )}
                        />
                    ) : (
                        <FormField label={t('smartInbox.form.mailInbox')}>
                            <StringField value={integration?.mailInbox || ''} disabled />
                        </FormField>
                    )}

                    {!hideEnabledField && (
                        <Controller
                            name="enabled"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('smartInbox.form.enabled')} error={formErrors.enabled}>
                                    <BooleanField {...field} value={field.value ? 'true' : ''} />
                                </FormField>
                            )}
                        />
                    )}

                    {!hideRelocatedFields && (
                        <>
                            <Controller
                                name="useSmartInboxAsCreatedBy"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('smartInbox.form.useSmartInboxAsCreatedBy')}
                                        error={formErrors.useSmartInboxAsCreatedBy}
                                    >
                                        <BooleanField {...field} value={field.value ? 'true' : ''} />
                                    </FormField>
                                )}
                            />

                            <Controller
                                name="isManualDocumentCreationEnabled"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('smartInbox.form.isManualDocumentCreationEnabled')}
                                        error={formErrors.isManualDocumentCreationEnabled}
                                    >
                                        <BooleanField {...field} value={field.value ? 'true' : ''} />
                                    </FormField>
                                )}
                            />
                        </>
                    )}

                    {!hideRelocatedFields && (
                        <Controller
                            name="ignoreReplyAndForwardedEmails"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('smartInbox.form.ignoreReplyAndForwardedEmails')}
                                    className="text-md"
                                    error={formErrors.ignoreReplyAndForwardedEmails}
                                >
                                    <BooleanField {...field} value={field.value ? 'true' : ''} />
                                </FormField>
                            )}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default GeneralFormFields;
